/** @jsxImportSource @emotion/react */
import styled from "@emotion/styled";
import { useRouter } from "next/router";
import React, { createRef, useState } from "react";
import ReCAPTCHA from "react-google-recaptcha";
import { toast } from "react-toastify";
import { useSetRecoilState } from "recoil";
import { api } from "../../../../api/GeneratedApi";
import { TestDriveBannerAtom } from "../../../../atoms/TestDriveBannerAtom";
import { UserAtom } from "../../../../atoms/UserAtom";
import { CAPTCHA_PUBLIC_KEY, SERVER_URL } from "../../../../config";
import CONFIG from "../../../../config.json";
import { IUserIRoute } from "../../../../generated/Api";
import { localStorageSetItem } from "../../../../utils/LocalStorage";
import PasscodeInput from "../../../PasscodeInput";
import StyledButton from "../../../StyledButton";
import { SignupFormSection } from "../../Form";
import {
  PasscodeButtonsWrapper,
  PasscodeContainer,
  PasscodeProps,
  PasscodeWrapper,
} from "../ThirdForm";

const ForgotTitle = styled.div`
  color: #fb972e;
  font-size: 18px;
  font-weight: 600;
  cursor: pointer;
  padding: 5px;

  margin-top: 30px;

  :hover {
    opacity: 0.7;
  }
`;

export default function LearnisticEnterPasscode({
  setSelectedForm,
  form,
  handleChangeForm,
}: PasscodeProps) {
  const [error, setError] = useState<string>("");
  const [passcode, setPasscode] = useState(["", "", "", ""]);
  const [isLoading, setIsLoading] = useState(false);
  const setUser = useSetRecoilState(UserAtom);
  const setTestDriveBanner = useSetRecoilState(TestDriveBannerAtom);
  const router = useRouter();

  const [showCapctha, setShowCaptcha] = useState(false);
  const recaptchaRef = createRef();

  const login = async ({ userUid, user, token }: any): Promise<void> => {
    if (!user.isActive) {
      toast("User is blocked", { type: "error" });
    } else if (!user) {
      toast("No user found!", { type: "error" });

      return;
    } else {
      const userToset = user as IUserIRoute & {
        billing: {
          admins?: number;
          externalUsers?: number;
          active?: boolean;
          testDriveExpiration?: number;
          testDriveActivated?: boolean;
        };
      };
      localStorageSetItem("USER_UID", user.userUid);
      localStorageSetItem("USER_TOKEN", token);

      localStorageSetItem("ADMIN_UID", user.adminUid || "");
      localStorageSetItem("ADMIN_AVATAR", user.avatar || "");

      localStorageSetItem("USER_EMAIL", "");
      localStorageSetItem("EXTRA_CARRIAGE", `${user.options?.extraCarriage}`);
      localStorageSetItem("FIRST_LINE_TITLE", `${user.options?.extraCarriage}`);
      setTestDriveBanner({
        show: !userToset.billing.active && userToset.billing.testDriveActivated,
        testDriveExpiration: userToset.billing.testDriveExpiration || 0,
      });
      setUser(userToset);
      router.replace("/complete-onboarding");
    }
  };

  async function handleClick() {
    if (passcode.includes("")) {
      toast("Incorrect passcode", { type: "error" });

      setError("Incorrect passcode");

      setIsLoading(false);
      return;
    }

    try {
      const responseLearnistic = await fetch(
        `${SERVER_URL}/auth/learnistic/login`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            phone: form.phone,
            password: passcode.join(""),
          }),
        }
      );
      const bodyLearnistic = await responseLearnistic.json();

      if (bodyLearnistic?.data?.ok) {
        const response = await fetch(`${SERVER_URL}/auth`, {
          method: "POST",
          headers: {
            Accept: "application/json",
            "Content-type": "application/json",
          },
          body: JSON.stringify({
            ...form,
            password: passcode.join(""),
            type: "LEARNISTIC",
            aliasName: form.sendFromName,
            alias: form.domainSuffix + "@" + form.domain,
            userAppName: form.appName,
            company: "",
            address: "",
          }),
        });

        let responseJSON: {
          data: { userUid: string; user: IUserIRoute; token: string };
        } = await response.json();

        login({
          userUid: responseJSON.data.userUid,
          user: responseJSON.data.user,
          token: responseJSON.data.token,
        });
      } else {
        setError("Incorrect passcode");
        toast("Invalid passcode", { type: "error" });
        setIsLoading(false);
        return;
      }
    } catch (error) {
      toast("Unexpected error", { type: "error" });
    } finally {
      setIsLoading(false);
    }
  }

  async function handleChangeCaptcha(value: string) {
    await api.auth.learnisticTempPasscodeCreate(form.phone, {
      captchaResponse: value,
    });

    setShowCaptcha(false);
    setSelectedForm(SignupFormSection.TemporyPasscodeLearnistic);
  }

  return (
    <PasscodeContainer>
      <PasscodeWrapper>
        <PasscodeInput
          passcode={passcode}
          handleChangePasscode={(newPassCode) => setPasscode(newPassCode)}
          error={error !== ""}
          handleChangeError={(newError) => setError(newError)}
        />
      </PasscodeWrapper>

      <PasscodeButtonsWrapper>
        <StyledButton
          onClick={() => setSelectedForm(SignupFormSection.Create)}
          inverted
        >
          Back
        </StyledButton>
        <StyledButton
          disable={passcode.includes("")}
          isLoading={isLoading}
          onClick={() => {
            if (isLoading) return;
            setIsLoading(true);
            handleClick();
          }}
        >
          Next
        </StyledButton>
      </PasscodeButtonsWrapper>

      {showCapctha ? (
        <ReCAPTCHA
          ref={recaptchaRef}
          sitekey={CAPTCHA_PUBLIC_KEY}
          onChange={handleChangeCaptcha}
        />
      ) : (
        <ForgotTitle
          onClick={async () => {
            setShowCaptcha(true);
          }}
        >
          Forgot passcode
        </ForgotTitle>
      )}
    </PasscodeContainer>
  );
}
